import { useState } from "react";
import {
  DeleteOutlined,
  EditOutlined,
  LeftCircleOutlined,
  LoadingOutlined,
  UsergroupAddOutlined,
  ForwardOutlined
} from "@ant-design/icons";
import {
  Button,
  Col,
  Descriptions,
  Row,
  Space,
  Table,
  Tag,
  notification,
  Popconfirm,
  Typography
} from "antd";
import { ColumnsType } from "antd/es/table";
import {
  useGetUserByIdQuery,
  useDeleteRoleMutation,
  useUpdateRoleMutation,
  useAssignUserRolesMutation,
  useTransferBookingsMutation
} from "../../redux/services/userApi";
import ErrorDetails from "../../components/ErrorDetails";
import AddRoleInUserModal from "./AddRoleInUserModal";
import TransferBookingsModal from "./TransferBookingsModal";
import { useNavigate, useParams } from "react-router-dom";
import { isUserHasRoleTypes } from "../../utils";

const { Text } = Typography;
const ViewUser = (props: any) => {
  const params = useParams();
  const navigate = useNavigate();

  const userId = params?.userId;

  const [loading, setLoading] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);

  const [api, contextHolder] = notification.useNotification();
  const [isEditMode, setEditMode] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showBookingsTransferModal, setShowBookingsTransferModal] = useState(false);
  const { data, error, isLoading, refetch } = useGetUserByIdQuery(userId);
  const user = data?.user;

  const [deleteRole] = useDeleteRoleMutation();
  const [updateRole] = useUpdateRoleMutation();
  const [assignUserRole] = useAssignUserRolesMutation();
  const [transferBookings] = useTransferBookingsMutation();

  const onRemove = async (role: any) => {
    const roleId = role?.id;
    setSelectedRoleId(roleId);
    // call delete user role API
    try {
      setLoading(true);
      await deleteRole({ userId, roleId }).unwrap();
      api.success({
        message: "Delete user role successful",
        placement: "top"
      });
      refetch();
      setLoading(false);
    } catch (error: any) {
      api.info({
        message: "Delete user role failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
      setLoading(false);
    }
  };

  const onUpdate = (role: any) => {
    setSelectedRoleId(role?.id);
    setSelectedRole(role);
    setEditMode(true);
    setShowModal(true);
  };

  const onUpdateRole = async (values: object, setLoading: any, form: any) => {
    try {
      setLoading(true);
      await updateRole(values).unwrap();
      api.success({
        message: "Role update to user successful",
        placement: "top"
      });
      form.resetFields();
      setLoading(false);
      setShowModal(false);
      setSelectedRoleId(null);
      refetch();
    } catch (error: any) {
      api.info({
        message: "Role update to user failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
      setLoading(false);
    }
  };

  const onGoBack = () => {
    navigate(-1);
  };

  const isFinanceController = (user: any): boolean => {
    return isUserHasRoleTypes(user, ["finance_controller"]);
  };

  const isCityManager = (user: any): boolean => {
    return isUserHasRoleTypes(user, ["city_manager"]);
  };

  const columns: ColumnsType<any> = [
    {
      title: "Role Type",
      dataIndex: "role_type",
      key: "role_type",
      render: (_, role) => (
        <Text>
          {role?.role_type} {role?.location?.name ? `(${role?.location?.name})` : ""}
        </Text>
      )
    },
    {
      title: "Action",
      key: "action",
      render: (_, role) => (
        <Space size="middle">
          <Popconfirm
            title="Are you sure you want to delete this role?"
            okText="Delete"
            placement="bottom"
            cancelText="Cancel"
            onConfirm={(event) => {
              onRemove(role);
            }}
            onCancel={(event: any) => event.stopPropagation()}
          >
            <Button
              icon={<DeleteOutlined />}
              danger
              loading={selectedRoleId === role.id ? loading : false}
              title="Remove"
            ></Button>
          </Popconfirm>
          <Button icon={<EditOutlined />} onClick={() => onUpdate(role)} title="Update"></Button>
        </Space>
      )
    }
  ];
  const onAssignUserRole = async (values: object, setLoading: any, form: any) => {
    try {
      setLoading(true);
      await assignUserRole(values).unwrap();
      api.success({
        message: "Role assign to user successful",
        placement: "top"
      });
      form.resetFields();
      setLoading(false);
      setShowModal(false);
      refetch();
    } catch (error: any) {
      api.info({
        message: "Role assign to user failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
      setLoading(false);
    }
  };

  const onTransfer = async (values: object, setLoading: any, form: any) => {
    try {
      setLoading(true);
      await transferBookings(values).unwrap();
      api.success({
        message: "Bookings transferred successfully",
        placement: "top"
      });
      form.resetFields();
      setLoading(false);
      setShowBookingsTransferModal(false);
      refetch();
    } catch (error: any) {
      api.info({
        message: "Bookings transfer failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
      setLoading(false);
    }
  };

  const onShowAssignRoleModal = () => {
    setSelectedRole(null);
    setShowModal(true);
  };

  return (
    <>
      {contextHolder}
      {user && !isLoading && (
        <>
          <Row style={{ marginBottom: 20 }}>
            <Col
              sm={24}
              style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
            >
              <h2>User Details</h2>
              {isCityManager(user) && (
                <Button
                  onClick={() => setShowBookingsTransferModal(true)}
                  size="middle"
                  type="default"
                  icon={<ForwardOutlined />}
                >
                  Transfer Bookings
                </Button>
              )}
              <Button onClick={onGoBack} size="middle" type="default" icon={<LeftCircleOutlined />}>
                Back
              </Button>
            </Col>
          </Row>
          <Descriptions
            title=""
            bordered
            column={1}
            labelStyle={{ fontWeight: 700, width: 300 }}
            size="middle"
          >
            <Descriptions.Item label="Name">{user.name || "-"}</Descriptions.Item>
            <Descriptions.Item label="Email">{user.email || "-"}</Descriptions.Item>
            <Descriptions.Item label="Mobile">{user.mobile || "-"}</Descriptions.Item>
            <Descriptions.Item label="Another Mobile">
              {user.alternate_mobile || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="User Type">{user.user_type || "-"}</Descriptions.Item>
            <Descriptions.Item label="Employee ID">{user.employee_id || "-"}</Descriptions.Item>
            <Descriptions.Item label="Aadhar Number">{user.aadhaar || "-"}</Descriptions.Item>
            {user?.user_type === "client" && (
              <Descriptions.Item label="Brand">{user?.brand.name || "-"}</Descriptions.Item>
            )}
            <Descriptions.Item label="Status">
              <Tag color={user.status === "active" ? "green" : "red"}>
                {user.status.toUpperCase()}
              </Tag>
            </Descriptions.Item>
          </Descriptions>
          <Row style={{ marginBottom: 20 }}>
            <Col
              sm={24}
              style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
            >
              <h2>Supervisor Details</h2>
            </Col>
          </Row>
          <Descriptions
            title=""
            bordered
            column={1}
            labelStyle={{ fontWeight: 700, width: 300 }}
            size="middle"
          >
            <Descriptions.Item label="Name">{user?.supervisor?.name || "-"}</Descriptions.Item>
            <Descriptions.Item label="Email">{user?.supervisor?.email || "-"}</Descriptions.Item>
            <Descriptions.Item label="Mobile">{user?.supervisor?.mobile || "-"}</Descriptions.Item>
          </Descriptions>
          {!(user?.user_type == "client" || isFinanceController(user)) && (
            <>
              <Row style={{ marginTop: 20, marginBottom: 20 }}>
                <Col
                  sm={24}
                  style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
                >
                  <h2>Roles</h2>
                  <Button
                    onClick={onShowAssignRoleModal}
                    size="middle"
                    type="default"
                    icon={<UsergroupAddOutlined />}
                  >
                    Assign Role
                  </Button>
                </Col>
              </Row>
              <Table
                rowKey="id"
                bordered
                columns={columns}
                pagination={false}
                dataSource={user?.roles}
                scroll={{ x: 400 }}
              />
            </>
          )}

          <AddRoleInUserModal
            isModalOpen={showModal}
            onCancel={() => {
              setShowModal(false);
              setEditMode(false);
            }}
            onUpdateRole={isEditMode && onUpdateRole}
            onAssignUserRole={!isEditMode && onAssignUserRole}
            user={user}
            role={selectedRole}
          />

          <TransferBookingsModal
            isModalOpen={showBookingsTransferModal}
            onCancel={() => {
              setShowBookingsTransferModal(false);
            }}
            onTransfer={onTransfer}
            user={user}
            role={selectedRole}
          />
        </>
      )}
      {isLoading && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh"
          }}
        >
          <LoadingOutlined style={{ fontSize: 24 }} spin />
        </div>
      )}
    </>
  );
};

export default ViewUser;
